import { forwardRef, useState, useRef } from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { useEditor, useNode } from '@craftjs/core';
import { layers } from 'app/spacing';
import IconButton from 'components/Kizen/IconButton';
import { KizenTypography } from 'app/typography';
import Icon, { IconSizing } from 'components/Kizen/Icon';
import Tooltip from 'components/Kizen/Tooltip';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import BasicModal from 'components/Modals/presets/BasicModal';
import { deleteNode, duplicateNode } from './actions';
import { useBuilderContext, useSetActiveNode } from '../BuilderContext';
import { canNodeBeDeleted, getChildren, isNodeSubmitButton } from '../utils';
import { DEFAULT_MAX_WIDTH } from '../settings';

const alignSelf = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export const CONTROL_HEIGHT = 15;
export const pixelValue = (value, fallback = undefined) => {
  const numValue = Number(value);
  return isNaN(numValue) ? fallback : `${numValue}px`;
};

const tooltipPopperConfig = {
  modifiers: {
    preventOverflow: { enabled: false },
  },
};

const DASH_LENGTHS = {
  Section: 5,
  Row: 4,
  Content: 3,
};

const Container = styled.div`
  position: relative;
  align-content: flex-start;
`;

export const ControlsWrapper = styled.div`
  display: flex;
  ${({ width = '100%' }) => `width: ${width};`}
  ${(props) => css`
    justify-content: ${alignSelf[props.alignment || 'center']};
    & > div {
      width: 100%;
      ${!props.fullWidth
        ? `max-width: ${props.maxWidth ? props.maxWidth : DEFAULT_MAX_WIDTH}px;`
        : ''}

      margin-top: ${pixelValue(props.containerMarginTop)};
      margin-right: ${pixelValue(props.containerMarginRight)};
      margin-bottom: ${pixelValue(props.containerMarginBottom)};
      margin-left: ${pixelValue(props.containerMarginLeft)};
    }
  `};
`;

const ControlsContainer = styled.div`
  z-index: ${({ modalLayer }) => layers.modals(modalLayer, 10)};
  position: absolute;
  top: 0;
  left: 0;
`;

const Outline = ({ children, show, gridLines, dashLength = 0, color }) => (
  <div className="flex flex-col relative">
    {children}
    {gridLines || show ? (
      <svg
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-0 left-0 pointer-events-none"
      >
        <rect
          width="100%"
          height="100%"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={show ? 0 : dashLength}
          strokeDashoffset="1"
          strokeLinecap="butt"
        />
      </svg>
    ) : null}
  </div>
);

const RightControlsContainer = styled(ControlsContainer)`
  left: unset;
  right: 0;
`;

const RightColumnControls = styled.div`
  display: flex;
  height: 15px;
  padding: 0 2px;
`;

const AddRemoveColumnControlsContainer = styled(RightColumnControls)`
  button {
    width: 16px;

    i:first-child {
      margin-right: 1px;
    }
  }
  background-color: ${({ removeColumn }) =>
    removeColumn
      ? 'var(--color-viz-cat-1-primary)'
      : 'var(--color-viz-cat-3-primary)'};
`;

const CarouselControlsContainer = styled(RightColumnControls)`
  width: 38px;
  justify-content: space-around;
  background-color: var(--color-viz-cat-1-primary);
`;

export const AddRemoveColumnControl = ({ removeColumn = false, onClick }) => {
  const { t } = useTranslation();
  const tooltipLabel = removeColumn ? t('Remove Column') : t('Add Column');

  return (
    <AddRemoveColumnControlsContainer removeColumn={removeColumn}>
      <OverlayTrigger
        popperConfig={tooltipPopperConfig}
        overlay={<Tooltip label={tooltipLabel} />}
      >
        <IconButton
          title={tooltipLabel}
          sizing="dense"
          color="var(--color-font-white)"
          onClick={onClick}
        >
          <IconSizing size="5px">
            <Icon icon={removeColumn ? 'minus' : 'plus'} />
          </IconSizing>
          <IconSizing size="9px">
            <Icon icon="table-columns" color="var(--color-font-white)" />
          </IconSizing>
        </IconButton>
      </OverlayTrigger>
    </AddRemoveColumnControlsContainer>
  );
};

export const CarouselControl = ({
  isActive,
  onPreviousClick,
  onNextClick,
  onPlayClick,
}) => {
  return (
    <CarouselControlsContainer>
      <IconButton
        sizing="dense"
        color="var(--color-font-white)"
        onClick={onPreviousClick}
      >
        <IconSizing size="9px">
          <Icon icon="chevron-left" />
        </IconSizing>
      </IconButton>
      <IconButton
        sizing="dense"
        color="var(--color-font-white)"
        onClick={onPlayClick}
      >
        <IconSizing size="9px">
          <Icon icon={isActive ? 'pause' : 'play'} />
        </IconSizing>
      </IconButton>
      <IconButton
        sizing="dense"
        color="var(--color-font-white)"
        onClick={onNextClick}
      >
        <IconSizing size="9px">
          <Icon icon="chevron-right" />
        </IconSizing>
      </IconButton>
    </CarouselControlsContainer>
  );
};

const Controls = styled.div`
  display: flex;
  z-index: ${({ modalLayer }) => layers.modals(modalLayer)};

  > div {
    height: ${CONTROL_HEIGHT}px;
    background-color: ${({ color }) => color};
    display: flex;
  }

  > div:first-child {
    justify-content: center;
    align-items: center;
    min-width: 60px;
    padding: 0 5px;
    margin-right: 1px;
    ${({ draggable }) =>
      draggable &&
      css`
        cursor: move;
      `}
  }
  > div:last-child {
    align-items: stretch;
    button {
      width: 16px;
    }
  }
`;

const ControlLabel = styled(KizenTypography)`
  && {
    color: ${({ color }) => color};
    font-family: unset;
  }
`;

const getUndeletableChildren = (isContactForm, id, query) => {
  const [childIds] = getChildren(id, query);
  return childIds
    .filter((i) => !canNodeBeDeleted(query.node(i).get(), isContactForm))
    .map((i) => query.node(i).get());
};

const Control = forwardRef(
  (
    {
      label,
      show,
      color,
      onSetActive,
      canDelete = true,
      canClone = true,
      mayHaveChildren = true,
      draggable = true,
      containerBorderWidth = 0,
      rightControl = null,
      customControls = null,
      width,
      children,
      ...other
    },
    ref
  ) => {
    const { t } = useTranslation();
    const {
      fullWidth,
      activeNode,
      gridLinesActive,
      modalLayer,
      isContactForm,
      onNodeDelete,
      onNodeDuplicate,
      clearContentSettingsTray,
    } = useBuilderContext();
    const setActiveNode = useSetActiveNode();
    const [controlHovered, setControlHovered] = useState(false);
    const controlHoveredRef = useRef(false);
    const [showDeletionModal, setShowDeletionModal] = useState(false);
    const [showBasicModal, setShowBasicModal] = useState(false);
    const [cannotDeleteMessage, setCannotDeleteMessage] = useState('');
    const { query, actions, hoveredId } = useEditor((state) => ({
      hoveredId: [...state.events.hovered.values()][0],
    }));
    const {
      id,
      parent,
      isDeletable,
      connectors: { drag },
    } = useNode((node) => ({
      id: node.id,
      parent: node.data.parent,
      isDeletable: canNodeBeDeleted(node, isContactForm),
    }));

    // When the controls are hovered, craft will register the hoveredId as the parent node. We track that extra
    // state to override the `show` prop. We also keep track of the prior renders controlHovered state to avoid
    // a flicker of the controls when the hoveredId is not current when hovering over the controls and then back
    // to the node.
    const isControlActive =
      (controlHoveredRef.current && parent === hoveredId) ||
      show ||
      controlHovered;
    controlHoveredRef.current = controlHovered;

    const closeBasicModal = (ev) => {
      ev?.stopPropagation();
      setShowBasicModal(false);
    };

    const handleDeleteClick = (ev) => {
      ev?.stopPropagation(); // ensure this click does not change the tray by selecting the parent element
      const undeletableChildren = getUndeletableChildren(
        isContactForm,
        id,
        query
      );
      if (undeletableChildren.length) {
        const nodeDisplayName = isNodeSubmitButton(undeletableChildren[0])
          ? 'The submit button'
          : undeletableChildren[0]?.data?.props.field?.displayName;
        setCannotDeleteMessage(
          `${nodeDisplayName} is a required field. Please move it outside of this component and retry deletion.`
        );
        setShowBasicModal(true);
      } else {
        setShowDeletionModal(true);
      }
    };

    const handleSetActiveNode = (event) => {
      event.stopPropagation();
      setActiveNode();
      onSetActive?.();
    };

    return (
      <ControlsWrapper fullWidth={fullWidth} width={width} {...other} ref={ref}>
        <Container>
          <span onClick={handleSetActiveNode}>
            <Outline
              gridLines={gridLinesActive}
              show={isControlActive}
              color={color}
              dashLength={DASH_LENGTHS[label]}
            >
              {children}
            </Outline>
          </span>
          {isControlActive && (
            <>
              <ControlsContainer
                modalLayer={modalLayer}
                onMouseEnter={() => setControlHovered(true)}
                onMouseLeave={() => setControlHovered(false)}
              >
                <Controls
                  draggable={draggable}
                  color={color}
                  modalLayer={modalLayer}
                >
                  <div
                    ref={draggable ? drag : null}
                    onClick={handleSetActiveNode}
                  >
                    <ControlLabel
                      as="span"
                      size="small"
                      weight="bold"
                      color="var(--color-font-white)"
                    >
                      {label}
                    </ControlLabel>
                  </div>
                  <div>
                    {canDelete && isDeletable && (
                      <OverlayTrigger
                        popperConfig={tooltipPopperConfig}
                        overlay={<Tooltip label={t('Delete')} />}
                      >
                        <IconButton
                          sizing="dense"
                          title={t('Delete')}
                          color="var(--color-font-white)"
                          onClick={handleDeleteClick}
                        >
                          <IconSizing size="9px">
                            <Icon icon="delete" />
                          </IconSizing>
                        </IconButton>
                      </OverlayTrigger>
                    )}
                    {canClone && (
                      <OverlayTrigger
                        popperConfig={tooltipPopperConfig}
                        overlay={<Tooltip label={t('Clone')} />}
                      >
                        <IconButton
                          sizing="dense"
                          title={t('Clone')}
                          color="var(--color-font-white)"
                          onClick={() => {
                            duplicateNode(id, {
                              isContactForm,
                              query,
                              actions,
                              contextDuplicateNode: onNodeDuplicate,
                            });
                          }}
                        >
                          <IconSizing size="9px">
                            <Icon icon="copy" />
                          </IconSizing>
                        </IconButton>
                      </OverlayTrigger>
                    )}
                    {customControls}
                  </div>
                </Controls>
              </ControlsContainer>
              {rightControl && (
                <RightControlsContainer>{rightControl}</RightControlsContainer>
              )}
            </>
          )}
        </Container>
        {showDeletionModal && (
          <ConfirmDeletionModal
            show={showDeletionModal}
            onHide={() => setShowDeletionModal(false)}
            onConfirm={(ev) => {
              ev?.stopPropagation();
              if (id === activeNode?.id) {
                clearContentSettingsTray();
              }
              deleteNode(id, {
                actions,
                query,
                contextDeleteNode: onNodeDelete,
              });
            }}
          >
            <KizenTypography>
              This will permanently delete the {label}
              {mayHaveChildren ? ' and its contents' : ''}.
            </KizenTypography>
          </ConfirmDeletionModal>
        )}
        {showBasicModal && (
          <BasicModal
            show={showBasicModal}
            onHide={closeBasicModal}
            onConfirm={closeBasicModal}
            buttonText={t('Close')}
            heading={t('Cannot Delete Component')}
            typeOfContent="text"
            actionBtnColor="blue"
            leftBtn={null}
          >
            <KizenTypography>{cannotDeleteMessage}</KizenTypography>
          </BasicModal>
        )}
      </ControlsWrapper>
    );
  }
);

Control.displayName = 'Control';

export default Control;
